import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext({
  theme: 'linkedin-blue-2025',
  setTheme: () => {},
  tokens: {
    colors: {},
    spacing: {},
    typography: {}
  }
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('linkedin-blue-2025');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('linkedin-blue-theme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // Apply theme to document and save to localStorage
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('linkedin-blue-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => 
      prevTheme === 'linkedin-blue-2025' ? 'high-contrast' : 'linkedin-blue-2025'
    );
  };

  const tokens = {
    colors: {
      brand: {
        primary: 'var(--brand-primary)',
        primaryHover: 'var(--brand-primary-hover)',
        primaryActive: 'var(--brand-primary-active)',
        primaryBorder: 'var(--brand-primary-border)',
        primaryHoverAlt: 'var(--brand-primary-hover-alt)',
        secondary: 'var(--brand-secondary)',
        accent: 'var(--brand-accent)'
      },
      ui: {
        surface: 'var(--ui-surface)',
        surfaceAlt: 'var(--ui-surface-alt)',
        surfaceHover: 'var(--ui-surface-hover)',
        surfaceHoverLight: 'var(--ui-surface-hover-light)',
        surfaceHoverAlt: 'var(--ui-surface-hover-alt)',
        surfaceLight: 'var(--ui-surface-light)',
        canvas: 'var(--ui-canvas)',
        border: 'var(--ui-border)',
        divider: 'var(--ui-divider)',
        focus: 'var(--ui-focus)',
        disabled: 'var(--ui-disabled)',
        disabledDark: 'var(--ui-disabled-dark)',
        shadow: 'var(--ui-shadow)',
        buttonDark: 'var(--ui-button-dark)',
        buttonDarker: 'var(--ui-button-darker)'
      },
      text: {
        primary: 'var(--text-primary)',
        secondary: 'var(--text-secondary)',
        inverse: 'var(--text-inverse)',
        link: 'var(--text-link)',
        muted: 'var(--text-muted)',
        tertiary: 'var(--text-tertiary)',
        dim: 'var(--text-dim)',
        dark: 'var(--text-dark)',
        neutral: 'var(--text-neutral)',
        black: 'var(--text-black)'
      },
      state: {
        success: 'var(--state-success)',
        successAlt: 'var(--state-success-alt)',
        successHover: 'var(--state-success-hover)',
        successButton: 'var(--state-success-button)',
        successDeep: 'var(--state-success-deep)',
        warning: 'var(--state-warning)',
        error: 'var(--state-error)',
        errorAlt: 'var(--state-error-alt)',
        errorButton: 'var(--state-error-button)',
        info: 'var(--state-info)'
      }
    },
    spacing: {
      0: 'var(--space-0)',
      1: 'var(--space-1)',
      2: 'var(--space-2)',
      3: 'var(--space-3)',
      4: 'var(--space-4)',
      5: 'var(--space-5)',
      8: 'var(--space-8)'
    },
    typography: {
      fontFamily: {
        primary: 'var(--font-family-primary)',
        code: 'var(--font-family-code)',
        icon: 'var(--font-family-icon)'
      },
      fontSize: {
        h1: 'var(--font-size-h1)',
        h2: 'var(--font-size-h2)',
        body: 'var(--font-size-body)',
        label: 'var(--font-size-label)',
        fine: 'var(--font-size-fine)',
        metricLarge: 'var(--font-size-metric-large)',
        metricMedium: 'var(--font-size-metric-medium)'
      },
      lineHeight: {
        h1: 'var(--line-height-h1)',
        h2: 'var(--line-height-h2)',
        body: 'var(--line-height-body)',
        label: 'var(--line-height-label)',
        fine: 'var(--line-height-fine)',
        metricLarge: 'var(--line-height-metric-large)',
        metricMedium: 'var(--line-height-metric-medium)'
      },
      fontWeight: {
        bold: 'var(--font-weight-bold)',
        semibold: 'var(--font-weight-semibold)',
        regular: 'var(--font-weight-regular)'
      }
    }
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    tokens
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

